import { inject, Injectable } from '@angular/core';
import { omit } from 'lodash-es';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { switchMap, tap } from 'rxjs/operators';

import { CreateDriverSiteSettingInput, GqlService } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';
import { batchPromises } from '@shared/utils';

import { DriverSiteSettingModel, DriverSiteStore } from './driver-site.store';

@Injectable({ providedIn: 'root' })
export class DriverSiteService {
  private driverSiteStore = inject(DriverSiteStore);
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);

  add(driverSite: DriverSiteSettingModel) {
    this.driverSiteStore.add(driverSite);
  }

  get() {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.driverSiteStore.setLoading(true);

        return this.gqlService.listDriverSiteSetting$().pipe(
          tap(({ data, errors, success }) => {
            this.driverSiteStore.set([]);

            if (success && data) {
              this.driverSiteStore.set(data);
            } else {
              this.overlayService.error(errors);
            }

            this.driverSiteStore.setLoading(false);
          })
        );
      })
    );
  }

  async remove(ids: string[]) {
    const promises = [];
    const allErrors: string[][] = [];

    for (const id of ids) {
      const prom = firstValueFrom(this.gqlService.removeDriverSiteSetting$(id)).then(
        ({ errors, success }) => {
          if (success) {
            this.driverSiteStore.remove(id);
          } else {
            allErrors.push(errors);
          }
        }
      );

      promises.push(prom);
    }

    await batchPromises(promises, (p) => p);

    return allErrors;
  }

  update(id: string, driverSite: Partial<DriverSiteSettingModel>) {
    this.driverSiteStore.update(id, driverSite);
  }

  async upsert(driverSiteSettings: (CreateDriverSiteSettingInput & { id?: string })[]) {
    const promises: Promise<void>[] = [];
    const allErrors: string[][] = [];

    for (const driverSiteSetting of driverSiteSettings) {
      if (driverSiteSetting.id) {
        const prom = firstValueFrom(
          this.gqlService.updateDriverSiteSetting$({
            id: driverSiteSetting.id,
            ...driverSiteSetting,
          })
        ).then(({ data, errors, success }) => {
          if (success && data) {
            this.driverSiteStore.update(data.id, data);
          } else {
            allErrors.push(errors);
          }
        });

        promises.push(prom);
      } else {
        const prom = firstValueFrom(
          this.gqlService.createDriverSiteSetting$({
            ...omit(driverSiteSetting, 'id'),
          })
        ).then(({ data, errors, success }) => {
          if (success && data) {
            this.driverSiteStore.add(data);
          } else {
            allErrors.push(errors);
          }
        });

        promises.push(prom);
      }
    }

    await batchPromises(promises, (p) => p);

    return allErrors;
  }
}
